import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useSelector } from 'react-redux';
import { isFetchingFeatureFlags } from '@core/selectors/classroom';
import { icons } from '@ublend-npm/aulaui-next';
import { useTheme, Chip } from '@ublend-npm/aulaui-core';
import { DARK } from '@core/constants/theme';
import { ENTER } from '../../../../utils/keyCodes';
import { SpaceLoader } from './Loaders';
import {
  SpaceSelectorContainer,
  Container,
  TabsWrapper,
  Alert,
} from './SpaceBar.styled';
import SpaceSelector from './SpaceSelector/SpaceSelector.container';
import SpaceSelectorV4 from './SpaceSelectorV4/SpaceSelector.container';
import { Tabs, Tab } from '../../common/Tabs';
import PermissionsModal from '../../common/PermissionsModal/PermissionsModal';
import { getCurrentSpaceId } from '@core/selectors/space';
import { analyticsTrackEvent } from '@core/utils/analytics';
import { MATERIALS_RBAC_PERMISSIONS_LINK_CLICKED } from '@core/constants/analytics';
import { RBAC_PERMISSIONS_ALERT_ITEM_TYPE } from '@app/utils/itemTypeForAnalytics';

const SpaceBar = ({
  currentTabId,
  shouldDisplaySpaceLoaders,
  shouldDisplayTabsPanel,
  tabs,
  onTabChange,
  accessingAsStaff,
  isRbacEnabledInSpace,
  roleName,
  userPermissions,
}) => {
  const [showWarning, setShowWarning] = useState(true);
  const [showRbacBanner, setShowRbacBanner] = useState(true);
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const { palette } = useTheme();
  const { spaceSelectorComponentUpgradeToV620252, webAppFtSpaceArea202504 } =
    useFlags();

  const fetchingFeatureFlags = useSelector(isFetchingFeatureFlags);
  const spaceId = useSelector(getCurrentSpaceId);

  console.log('spaceId', spaceId);

  if (fetchingFeatureFlags) {
    return null;
  }

  const spaceSelector =
    webAppFtSpaceArea202504 ? null : shouldDisplaySpaceLoaders ? (
      <SpaceLoader />
    ) : spaceSelectorComponentUpgradeToV620252 ? (
      <SpaceSelector />
    ) : (
      <SpaceSelectorV4 />
    );
  const tabIndex = tabs.findIndex((tab) => tab.id === currentTabId);

  return (
    !webAppFtSpaceArea202504 && (
      <>
        <Container
          shouldShowBottomShadow={shouldDisplayTabsPanel}
          bgColor={palette.mode === DARK ? palette.background.paper : '#fff'}
          aria-label="Main"
        >
          {accessingAsStaff && showWarning && (
            <Alert>
              Some actions are not available because you are viewing this space
              via staff access.
              <icons.Close
                tabIndex="0"
                role="button"
                onClick={() => setShowWarning(false)}
                onKeyDown={(e) => e.keyCode === ENTER && setShowWarning(false)}
                style={{ fontSize: '20px', color: 'black', cursor: 'pointer' }}
              />
            </Alert>
          )}
          {isRbacEnabledInSpace &&
            roleName &&
            roleName !== 'Student' &&
            roleName !== 'Educator' &&
            showRbacBanner && (
              <Alert>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flexWrap: 'wrap',
                  }}
                >
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <span style={{ fontWeight: 500 }}>Current Role:</span>
                    <Chip
                      label={roleName}
                      size="small"
                      style={{
                        height: '24px',
                        backgroundColor: palette.secondary.main,
                        color: '#fff',
                        fontWeight: 500,
                      }}
                    />
                  </span>
                  <span>
                    It looks like your access is limited in this area. You can
                    check your{' '}
                    <span
                      onClick={() => {
                        analyticsTrackEvent(
                          MATERIALS_RBAC_PERMISSIONS_LINK_CLICKED,
                          {
                            spaceId,
                            roleName,
                            itemType: RBAC_PERMISSIONS_ALERT_ITEM_TYPE,
                          }
                        );
                        setPermissionsModalOpen(true);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          analyticsTrackEvent(
                            MATERIALS_RBAC_PERMISSIONS_LINK_CLICKED,
                            {
                              spaceId,
                              roleName,
                              itemType: RBAC_PERMISSIONS_ALERT_ITEM_TYPE,
                            }
                          );
                          setPermissionsModalOpen(true);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      style={{
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      }}
                    >
                      permissions
                    </span>{' '}
                    to see what is available to you. This restriction affects
                    Materials only.
                  </span>
                </span>
                <icons.Close
                  tabIndex="0"
                  role="button"
                  onClick={() => setShowRbacBanner(false)}
                  onKeyDown={(e) =>
                    e.keyCode === ENTER && setShowRbacBanner(false)
                  }
                  style={{
                    fontSize: '20px',
                    color: 'black',
                    cursor: 'pointer',
                  }}
                />
              </Alert>
            )}
          <SpaceSelectorContainer
            showWarning={
              (accessingAsStaff && showWarning) ||
              (isRbacEnabledInSpace && roleName && showRbacBanner)
            }
          >
            {spaceSelector}
          </SpaceSelectorContainer>
          {shouldDisplayTabsPanel && (
            <TabsWrapper>
              <Tabs
                id="space-settings-tabs"
                value={tabIndex}
                onChange={onTabChange}
              >
                {tabs.map((tab) => (
                  <Tab
                    key={tab.id}
                    label={tab.label}
                    icon={tab.icon}
                    aria-label={tab.documentTitlePrefix}
                    data-testid={tab.id}
                  />
                ))}
              </Tabs>
            </TabsWrapper>
          )}
        </Container>
        {isRbacEnabledInSpace && (
          <PermissionsModal
            open={permissionsModalOpen}
            onClose={() => setPermissionsModalOpen(false)}
            roleName={roleName}
            userPermissions={userPermissions || []}
          />
        )}
      </>
    )
  );
};

SpaceBar.propTypes = {
  shouldDisplaySpaceLoaders: PropTypes.bool,
  shouldDisplayTabsPanel: PropTypes.bool,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      documentTitlePrefix: PropTypes.string.isRequired,
      label: PropTypes.node,
    })
  ).isRequired,
  currentTabId: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  accessingAsStaff: PropTypes.bool.isRequired,
  isRbacEnabledInSpace: PropTypes.bool,
  roleName: PropTypes.string,
  userPermissions: PropTypes.arrayOf(PropTypes.string),
};

SpaceBar.defaultProps = {
  shouldDisplaySpaceLoaders: false,
  shouldDisplayTabsPanel: true,
  isRbacEnabledInSpace: false,
  roleName: null,
  userPermissions: [],
};

export default React.memo(SpaceBar);
